import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Switch, FormControlLabel, TextField, Button,
  Alert, CircularProgress, Divider, Grid, Card, CardContent, Chip, Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const DEFAULT_TEMPLATES = {
  confirmation: 'Hola {tutor}, confirma tu cita para {mascota} el {fecha} a las {hora} con Dr/a. {profesional}. Confirma o cancela aquí: {link}',
  reminder: 'Hola {tutor}, le recordamos que {mascota} tiene cita el {fecha} a las {hora} con Dr/a. {profesional}. ¡Los esperamos!',
  waitlistNotify: 'Hola {tutor}, se liberó un cupo para {mascota} el {fecha} a las {hora}. Responde para confirmar.',
};

const NotificationsConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [config, setConfig] = useState({
    enabled: false,
    whatsappToken: '',
    whatsappPhoneNumberId: '',
    whatsappBusinessId: '',
    whatsappApiVersion: 'v18.0',
    senderPhone: '',
    confirmationTemplate: DEFAULT_TEMPLATES.confirmation,
    reminderTemplate: DEFAULT_TEMPLATES.reminder,
    waitlistTemplate: DEFAULT_TEMPLATES.waitlistNotify,
    reminderHoursBefore: 24,
    testPhone: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'notifications'));
        if (snap.exists()) {
          setConfig((prev) => ({ ...prev, ...snap.data() }));
        }
      } catch (e) {
        console.error('Error cargando config de notificaciones:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'notifications'), {
        ...config,
        updatedAt: new Date(),
      }, { merge: true });
      setSnackbar({ open: true, message: 'Configuración guardada', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al guardar: ' + e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!config.whatsappToken || !config.whatsappPhoneNumberId) {
      setTestResult({ ok: false, error: 'Faltan credenciales de WhatsApp Business API' });
      return;
    }
    if (!config.testPhone) {
      setTestResult({ ok: false, error: 'Ingresa un número de teléfono de prueba' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const phone = config.testPhone.replace(/\D/g, '');
      const url = `https://graph.facebook.com/${config.whatsappApiVersion}/${config.whatsappPhoneNumberId}/messages`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.whatsappToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messaging_product: 'whatsapp',
          to: phone,
          type: 'text',
          text: { body: '✅ Mensaje de prueba desde HCV UDLA - Notificaciones configuradas correctamente.' },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setTestResult({ ok: true, message: 'Mensaje enviado correctamente. Revisa el WhatsApp.' });
      } else {
        setTestResult({ ok: false, error: data.error?.message || 'Error al enviar' });
      }
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
    } finally {
      setTesting(false);
    }
  };

  const update = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  const isConfigured = config.whatsappToken && config.whatsappPhoneNumberId;

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Notificaciones Automáticas</Typography>
          <Typography variant="body2" color="text.secondary">
            Configura el envío automático de confirmaciones y recordatorios por WhatsApp Business API
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={config.enabled} onChange={(e) => update('enabled', e.target.checked)} color="primary" />}
          label={config.enabled ? 'Activado' : 'Desactivado'}
        />
      </Box>

      {/* Estado */}
      <Alert
        severity={config.enabled && isConfigured ? 'success' : 'warning'}
        icon={config.enabled && isConfigured ? <CheckCircleIcon /> : <WarningAmberIcon />}
        sx={{ mb: 3 }}
      >
        {config.enabled && isConfigured
          ? 'Las notificaciones automáticas están activas. Se enviarán al crear citas y 24h antes.'
          : config.enabled && !isConfigured
            ? 'Notificaciones activadas pero faltan credenciales. Completa los campos abajo.'
            : 'Notificaciones desactivadas. Activa el toggle e ingresa las credenciales de WhatsApp Business API.'}
      </Alert>

      {/* Credenciales WhatsApp Business API */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Credenciales WhatsApp Business API</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Obtén estas credenciales en{' '}
          <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer">developers.facebook.com</a>
          {' '}→ tu app → WhatsApp → Configuración de la API.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Token de acceso permanente" type="password"
              value={config.whatsappToken}
              onChange={(e) => update('whatsappToken', e.target.value)}
              placeholder="EAAG..."
              helperText="Token de acceso del sistema (System User Access Token)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Phone Number ID"
              value={config.whatsappPhoneNumberId}
              onChange={(e) => update('whatsappPhoneNumberId', e.target.value)}
              placeholder="1234567890"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="WhatsApp Business Account ID"
              value={config.whatsappBusinessId}
              onChange={(e) => update('whatsappBusinessId', e.target.value)}
              placeholder="1234567890"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Versión de API"
              value={config.whatsappApiVersion}
              onChange={(e) => update('whatsappApiVersion', e.target.value)}
              placeholder="v18.0"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Número emisor (opcional)"
              value={config.senderPhone}
              onChange={(e) => update('senderPhone', e.target.value)}
              placeholder="+56 2 1234 5678"
              helperText="Número de WhatsApp Business registrado"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Plantillas de mensajes */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Plantillas de mensajes</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Variables disponibles: <Chip size="small" label="{tutor}" /> <Chip size="small" label="{mascota}" />{' '}
          <Chip size="small" label="{fecha}" /> <Chip size="small" label="{hora}" />{' '}
          <Chip size="small" label="{profesional}" /> <Chip size="small" label="{especialidad}" />{' '}
          <Chip size="small" label="{link}" />
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={2} label="Confirmación de cita"
              value={config.confirmationTemplate}
              onChange={(e) => update('confirmationTemplate', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={2} label="Recordatorio (24h antes)"
              value={config.reminderTemplate}
              onChange={(e) => update('reminderTemplate', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={2} label="Notificación de lista de espera"
              value={config.waitlistTemplate}
              onChange={(e) => update('waitlistTemplate', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth type="number" label="Horas antes para enviar recordatorio"
              value={config.reminderHoursBefore}
              onChange={(e) => update('reminderHoursBefore', parseInt(e.target.value, 10) || 24)}
              inputProps={{ min: 1, max: 72 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Prueba */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Probar envío</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <TextField
            label="Número de prueba (con código de país)"
            value={config.testPhone}
            onChange={(e) => update('testPhone', e.target.value)}
            placeholder="56912345678"
            sx={{ flex: 1, minWidth: 250 }}
            helperText="Formato: 56 + 9 + número (sin + ni espacios)"
          />
          <Button
            variant="outlined" startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleTest} disabled={testing}
          >
            Enviar prueba
          </Button>
        </Box>
        {testResult && (
          <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
            {testResult.ok ? testResult.message : testResult.error}
          </Alert>
        )}
      </Paper>

      {/* Guardar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="contained" size="large" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{ borderRadius: 2 }}
        >
          Guardar configuración
        </Button>
      </Box>

      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      )}
    </Box>
  );
};

export default NotificationsConfig;
